import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AnimatePresence, motion } from 'framer-motion';
import './App.css';

import Navbar from './components/Navbar';
import Footer from './components/Footer';
import StickyMobileCTA from './components/StickyMobileCTA';

import Home from './pages/Home';
import Checkout from './pages/Checkout';
import Legal from './pages/Legal';
import AdminLogin from './pages/admin/AdminLogin';
import Dashboard from './pages/admin/Dashboard';
import Orders from './pages/admin/Orders';
import OrderDetail from './pages/admin/OrderDetail';
import Stock from './pages/admin/Stock';
import Capacity from './pages/admin/Capacity';
import Partners from './pages/admin/Partners';
import Settings from './pages/admin/Settings';
import Affiliates from './pages/admin/Affiliates';
import PromoCodes from './pages/admin/PromoCodes';
import Withdrawals from './pages/admin/Withdrawals';
import TeamManagement from './pages/admin/TeamManagement';
import AffiliateLogin from './pages/affiliate/AffiliateLogin';
import AffiliateRegister from './pages/affiliate/AffiliateRegister';
import AffiliateDashboard from './pages/affiliate/AffiliateDashboard';

// Page transition wrapper for smooth transitions
const PageTransition: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -8 }}
    transition={{
      duration: 0.4,
      ease: [0.4, 0, 0, 1],
    }}
  >
    {children}
  </motion.div>
);

const AnimatedRoutes: React.FC = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><Home /></PageTransition>} />
        <Route path="/checkout" element={<PageTransition><Checkout /></PageTransition>} />
        <Route path="/checkout/:orderNumber" element={<PageTransition><Checkout /></PageTransition>} />
        <Route path="/terms" element={<PageTransition><Legal /></PageTransition>} />
        <Route path="/admin/login" element={<PageTransition><AdminLogin /></PageTransition>} />
        <Route path="/admin/dashboard" element={<PageTransition><Dashboard /></PageTransition>} />
        <Route path="/admin/orders" element={<PageTransition><Orders /></PageTransition>} />
        <Route path="/admin/orders/:id" element={<PageTransition><OrderDetail /></PageTransition>} />
        <Route path="/admin/stock" element={<PageTransition><Stock /></PageTransition>} />
        <Route path="/admin/capacity" element={<PageTransition><Capacity /></PageTransition>} />
        <Route path="/admin/affiliates" element={<PageTransition><Affiliates /></PageTransition>} />
        <Route path="/admin/promo-codes" element={<PageTransition><PromoCodes /></PageTransition>} />
        <Route path="/admin/withdraws" element={<PageTransition><Withdrawals /></PageTransition>} />
        <Route path="/admin/partners" element={<PageTransition><Partners /></PageTransition>} />
        <Route path="/admin/team" element={<PageTransition><TeamManagement /></PageTransition>} />
        <Route path="/admin/settings" element={<PageTransition><Settings /></PageTransition>} />
        <Route path="/affiliate/login" element={<PageTransition><AffiliateLogin /></PageTransition>} />
        <Route path="/affiliate/register" element={<PageTransition><AffiliateRegister /></PageTransition>} />
        <Route path="/affiliate/dashboard" element={<PageTransition><AffiliateDashboard /></PageTransition>} />
      </Routes>
    </AnimatePresence>
  );
};

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const isAffiliate = location.pathname.startsWith('/affiliate');

  if (isAdmin || isAffiliate) {
    return (
      <div className="admin-layout">
        <main className="app-main">{children}</main>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <Navbar />
      <main className="app-main">{children}</main>
      <Footer />
      <StickyMobileCTA />
    </div>
  );
};

function App() {
  return (
    <Router>
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'rgba(16, 16, 22, 0.95)',
            color: '#EEEEF1',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: '14px',
            padding: '14px 22px',
            fontSize: '0.8125rem',
            fontFamily: "'Inter', sans-serif",
            boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
            backdropFilter: 'blur(20px)',
          },
          success: {
            iconTheme: { primary: '#34D399', secondary: '#101016' },
          },
          error: {
            iconTheme: { primary: '#F87171', secondary: '#101016' },
          },
        }}
      />
      <Layout>
        <AnimatedRoutes />
      </Layout>
    </Router>
  );
}

export default App;
